import { useState } from "react";
import AppShell from "@/components/AppShell";
import { GoogleDrivePicker } from "@/components/GoogleDrivePicker";
import ManualMatch from "@/components/ManualMatch";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle2, XCircle } from "lucide-react";

export default function AdminTryoutUpload() {
  const [batchName, setBatchName] = useState("");
  const [kelas, setKelas] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const submit = async () => {
    if (!batchName.trim()) { toast.error("Beri nama simulasi dulu"); return; }
    if (!file) { toast.error("Pilih file PDF hasil TryOut"); return; }
    setLoading(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("batch_name", batchName.trim());
    fd.append("kelas", kelas.trim());
    try {
      const { data } = await api.post("/admin/tryout/upload-batch", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = data.job_id;
      let tries = 0;
      const poll = async () => {
        tries++;
        try {
          const { data: st } = await api.get(`/admin/tryout/batch-status/${jobId}`);
          if (st.status === "done") { setResult(st); toast.success(`${st.matched} siswa dicocokkan`); setLoading(false); setRefreshKey((k) => k + 1); }
          else if (st.status === "error") { toast.error("Gagal memproses PDF"); setLoading(false); }
          else if (tries < 100) { setTimeout(poll, 3000); }
          else { toast.error("Proses terlalu lama, coba lagi"); setLoading(false); }
        } catch { setLoading(false); toast.error("Gagal memeriksa status"); }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengunggah PDF");
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto">
        <h1 className="font-heading font-black text-3xl text-[#1A1C1A] mb-1">Upload Hasil TryOut per Batch</h1>
        <p className="text-[#5C5F5C] mb-6">Upload PDF ranking dari Bimbel Talenta. Sistem membaca tabel & mendistribusikan hasil ke Profilku tiap siswa.</p>

        <div className="clay-card p-6 space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#1A1C1A] block mb-1">Nama Simulasi</label>
            <input value={batchName} onChange={(e) => setBatchName(e.target.value)} data-testid="batch-name-input"
              placeholder="Contoh: Simulasi 8 — UTBK SNBT 2026"
              className="w-full h-11 px-4 rounded-xl border border-[#E5E0D8] focus:border-[#E25E3E] outline-none" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#1A1C1A] block mb-1">Kelas / Grup (opsional)</label>
            <input value={kelas} onChange={(e) => setKelas(e.target.value)} data-testid="batch-kelas-input"
              placeholder="Contoh: Kelas 12 IPA 1"
              className="w-full h-11 px-4 rounded-xl border border-[#E5E0D8] focus:border-[#E25E3E] outline-none" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#1A1C1A] block mb-1">File PDF</label>
            <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="batch-file-input"
              className="w-full text-sm text-[#5C5F5C] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#1A1C1A] file:text-white file:text-sm" />
            <div className="mt-2"><GoogleDrivePicker onPicked={(fs) => setFile(fs[0])} /></div>
            {file && <p className="text-xs text-[#5C5F5C] mt-1">{file.name}</p>}
          </div>
          <button onClick={submit} disabled={loading} data-testid="batch-upload-btn"
            className="px-6 py-3 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors inline-flex items-center gap-2 disabled:opacity-60">
            <Upload className="h-4 w-4" /> {loading ? "Memproses PDF…" : "Upload & Distribusikan"}
          </button>
        </div>

        {result && (
          <div className="clay-card p-6 mt-6" data-testid="batch-result">
            <h2 className="font-heading font-bold text-lg text-[#1A1C1A] mb-3">Ringkasan: {result.batch_name}</h2>
            <div className="flex gap-4 mb-4">
              <div className="flex items-center gap-2 text-[#4A6B53]"><CheckCircle2 className="h-5 w-5" /> <b>{result.matched}</b> cocok</div>
              <div className="flex items-center gap-2 text-[#D9381E]"><XCircle className="h-5 w-5" /> <b>{result.unmatched?.length || 0}</b> tidak ditemukan</div>
              <div className="text-[#5C5F5C]">Total: {result.total}</div>
            </div>
            {result.unmatched?.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-[#1A1C1A] mb-1">Nama yang tidak cocok dengan akun:</p>
                <div className="flex flex-wrap gap-1.5">
                  {result.unmatched.map((n, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#D9381E]/10 text-[#D9381E]">{n}</span>)}
                </div>
                <p className="text-xs text-[#5C5F5C] mt-2">Cocokkan nama ini secara manual ke akun siswa di panel "Pencocokan Nama Manual" di bawah.</p>
              </div>
            )}
          </div>
        )}

        <ManualMatch refreshKey={refreshKey} />
      </div>
    </AppShell>
  );
}
